/* Version: 3.5.155 */
'use client';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import { useContext, useMemo, Children, Fragment } from 'react';
import PropTypes from 'prop-types';
import Button from '../Button/index.js';
import { BREAKPOINTS } from '../../spacing/index.js';
import { MOBILE_MENU_VARIANT } from './constants.js';
import SideNavContext from './SideNavContext.js';
import SideNavHeading from './SideNavHeading.js';
import { MobileSubNavWrapper, MobileHeaderItem, SubNavList, SubNavWrapper } from './styledComponents.js';
import { addRefToFirstChild } from './utils.js';
import WordmarkLogo from './WordmarkLogo.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import useDerivedState from '../../hooks/useDerivedState.js';
import useId from '../../hooks/useId.js';
import useTheme from '../../hooks/useTheme.js';
import useNavigation from '../../hooks/useNavigation.js';
import useMedia from '../../hooks/useMedia.js';
import { BUTTON_VARIANT } from '../Button/constants.js';

var SubNav = function SubNav(_ref) {
  var controlledId = _ref.id,
    _ref$isSubNavExpanded = _ref.isSubNavExpanded,
    isSubNavExpanded = _ref$isSubNavExpanded === undefined ? false : _ref$isSubNavExpanded,
    backButtonLabel = _ref.backButtonLabel,
    children = _ref.children;
  var _useDerivedState = useDerivedState(useId(), controlledId),
    _useDerivedState2 = _slicedToArray(_useDerivedState, 1),
    id = _useDerivedState2[0];
  var theme = useTheme();
  var _useNavigation = useNavigation(),
    subNavWrapperRef = _useNavigation.subNavWrapperRef;
  var _useContext = useContext(SideNavContext),
    logo = _useContext.logo,
    wordmarkLogoProps = _useContext.wordmarkLogoProps,
    isWideMainNav = _useContext.isWideMainNav,
    isMobileNavExpanded = _useContext.isMobileNavExpanded,
    setExpandedParentKey = _useContext.setExpandedParentKey,
    subNavRef = _useContext.subNavRef;
  var showHamburgerMenu = useMedia(["(max-width: ".concat(BREAKPOINTS.MAX_1024, "px)")],
  // media queries
  [true],
  // values corresponding to media query
  false // default value
  );

  // Split the children in to separate lists grouped by headings
  var headingsAndChildren = useMemo(function () {
    var childrenAsArray = Children.toArray(children);
    var groupedChildren = childrenAsArray.reduce(function (groups, item) {
      if (item.type === SideNavHeading) {
        groups.push({
          heading: item,
          children: []
        });
      } else {
        groups[groups.length - 1].children.push(item);
      }
      return groups;
    },
    // In the case there is no heading, we still need something to add
    // the children to so the initial value should be an empty group
    [{
      heading: null,
      children: []
    }]);

    // When there was a heading as first child, the first group will be empty and
    // we can just remove it.
    if (groupedChildren[0].heading === null && groupedChildren[0].children.length === 0) {
      groupedChildren.shift();
    }
    return groupedChildren;
  }, [children]);
  return showHamburgerMenu ? /*#__PURE__*/jsxs(MobileSubNavWrapper, {
    id: "sub-nav-".concat(id),
    "data-testid": "sub-nav-".concat(id),
    isSubNavExpanded: isSubNavExpanded,
    isMobileNavExpanded: isMobileNavExpanded,
    theme: theme,
    children: [theme.sideNav.mobile.variant === MOBILE_MENU_VARIANT.OVERLAY && /*#__PURE__*/jsx(MobileHeaderItem, {
      theme: theme,
      children: /*#__PURE__*/jsx(Button, {
        "data-testid": "back-button-".concat(id),
        variant: BUTTON_VARIANT.TERTIARY_BACK,
        customStyle: {
          buttonStyle: {
            paddingLeft: '14px',
            height: '100%'
          }
        },
        onClick: function onClick() {
          return setExpandedParentKey();
        },
        children: backButtonLabel
      })
    }), headingsAndChildren.map(function (group) {
      return /*#__PURE__*/jsxs(Fragment, {
        children: [group.heading, /*#__PURE__*/jsx(SubNavList, {
          children: group.children
        })]
      }, group.heading);
    })]
  }) : isSubNavExpanded && /*#__PURE__*/jsxs(SubNavWrapper, {
    ref: subNavWrapperRef,
    id: "sub-nav-".concat(id),
    "data-testid": "sub-nav-".concat(id),
    isWideMainNav: isWideMainNav,
    theme: theme,
    children: [/*#__PURE__*/jsx(WordmarkLogo, _objectSpread({
      logo: logo
    }, wordmarkLogoProps)), headingsAndChildren.map(function (group) {
      return /*#__PURE__*/jsxs(Fragment, {
        children: [group.heading, /*#__PURE__*/jsx(SubNavList, {
          children: addRefToFirstChild(group.children, subNavRef)
        })]
      }, group.heading);
    })]
  }, "sub-nav-".concat(id));
};
process.env.NODE_ENV !== "production" ? SubNav.propTypes = {
  id: PropTypes.string,
  isSubNavExpanded: PropTypes.bool,
  backButtonLabel: PropTypes.node,
  children: PropTypes.node.isRequired
} : undefined;
process.env.NODE_ENV !== "production" ? SubNav.displayName = 'SubNav' : undefined;

export { SubNav as default };
